import { useState, type MouseEvent } from 'react';

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PostBusinessMarkers } from '@/features/business/PostBusinessMarkers';
import { TypeBadge } from '@/components/common/TypeBadge';
import { useSession } from '@/features/auth/useSession';
import { BlockDialog, type BlockTarget } from '@/features/discovery/BlockDialog';
import { ReportDialog } from '@/features/discovery/ReportDialog';
import type { ReportTarget } from '@/features/discovery/useDiscovery';
import { VoteButton } from '@/features/interaction/VoteButton';
import { useInteractionGate } from '@/features/interaction/useInteractionGate';
import { useToggleBookmark, useTogglePostVote } from '@/features/interaction/useInteraction';
import { flagEmoji, REGISTERS } from '@/features/posts/registers';
import { useRelativeTime } from '@/lib/dates';
import type { Post } from '@/lib/schemas/post';
import { motion, radius } from '@/theme';

import { EditPostDialog } from './EditPostDialog';
import { VerifiedMark } from '@/features/business/VerifiedMark';
import { PostBody } from './PostBody';
import { PostMedia } from './PostMedia';

interface PostCardProps {
  post: Post;
  /** Detail view: no clamping, no card link. */
  expanded?: boolean;
  /**
   * Where the card sits in the page's heading order.
   *
   * The feed puts cards directly under its `h1`, so `h2` is right there. On a
   * page that groups cards under section headings — the hall of fame groups by
   * month — an `h2` per card would make the outline claim each post is a
   * sibling of the month it belongs to.
   */
  headingLevel?: 'h2' | 'h3';
  onTagClick?: (tag: string) => void;
  onDelete?: (post: Post) => void;
}

/**
 * The post card (spec §9.2).
 *
 * Every register is carried by its 4px accent bar, a tinted wash, a coloured
 * border, an icon and a text label — colour is never the only signal.
 *
 * Voting and bookmarking are optimistic: the count moves the instant the button
 * is pressed and rolls back if the request fails. Signed-out and unverified
 * readers get the controls disabled with a tooltip saying why, rather than a
 * dead button or no button at all.
 */
export function PostCard({
  post,
  expanded = false,
  headingLevel = 'h2',
  onTagClick,
  onDelete,
}: PostCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [blockTarget, setBlockTarget] = useState<BlockTarget | null>(null);
  const register = REGISTERS[post.type];

  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const { user } = useSession();

  const disabledReason = useInteractionGate();
  const toggleVote = useTogglePostVote();
  const toggleBookmark = useToggleBookmark();

  const authorName = post.isAnonymous
    ? t('post.anonymous')
    : (post.author?.displayName ?? t('post.unknownAuthor'));

  // Safety actions need somebody to act on and somebody to act: an anonymous
  // post has no author to block, and your own post is not yours to report.
  //
  // Ownership is decided by the author id, not by `canDelete` — a moderator may
  // delete anything, which would have hidden Report on every post in the feed.
  // The owner of an anonymous post is always told it is theirs, so the check
  // holds there too.
  const isMine = Boolean(user && post.author?.id === user.id);
  const otherAuthor = post.author && !post.isAnonymous && user && !isMine ? post.author : null;
  const canReport = Boolean(user) && !isMine;
  const timeAgo = relativeTime(post.createdAt);

  return (
    <Card
      component="article"
      aria-labelledby={`post-${post.id}-heading`}
      sx={{
        display: 'flex',
        overflow: 'hidden',
        borderColor: (theme) => theme.palette[register.accent].border,
        backgroundColor: (theme) => theme.palette[register.accent].softer,
        transition: `transform ${motion.fast}ms ${motion.easing}, box-shadow ${motion.fast}ms ${motion.easing}`,
        ...(expanded
          ? {}
          : {
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: (theme) => theme.shadows[2],
              },
            }),
      }}
    >
      {/* The 4px accent bar. Decorative — the badge below states the register in words. */}
      <Box
        aria-hidden
        sx={{
          width: 4,
          flexShrink: 0,
          backgroundColor: (theme) => theme.palette[register.accent].main,
        }}
      />

      <Stack spacing={1.5} sx={{ p: 2, flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {/* An anonymous post has no profile to link to — that is the point. */}
          <Box
            {...(post.author && !post.isAnonymous
              ? {
                  component: RouterLink,
                  to: `/u/${post.author.username}`,
                  // The avatar image is decorative — the name beside it is the
                  // text. Without a label this link has no accessible name at
                  // all, because `alt=""` is the right thing for the image.
                  'aria-label': authorName,
                }
              : {})}
            // Centred against the two meta lines rather than pinned to the
            // first: an avatar level with the name alone sits high against a
            // header that is two rows tall.
            sx={{ display: 'flex', textDecoration: 'none', alignSelf: 'center' }}
          >
            <Avatar
              src={post.author?.avatarUrl ?? undefined}
              alt=""
              sx={{ width: 36, height: 36, fontSize: '0.875rem' }}
            >
              {post.isAnonymous ? '?' : authorName.charAt(0).toUpperCase()}
            </Avatar>
          </Box>

          <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* One line, not a wrapping row: with the register badge taking the
                right-hand side, a wrapping row put the name, the handle and the
                timestamp on three separate lines of a phone. The name is the
                part that gives way — a truncated name still identifies someone,
                a missing timestamp tells you nothing. */}
            <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
                {post.author && !post.isAnonymous ? (
                  <Box
                    component={RouterLink}
                    to={`/u/${post.author.username}`}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {authorName}
                    <VerifiedMark status={post.author.business?.verificationStatus} />
                  </Box>
                ) : (
                  authorName
                )}
                {post.author && !post.isAnonymous && (
                  <Box
                    component="span"
                    // The handle repeats what the name and the avatar link
                    // already say; on a phone the room is better spent on the
                    // name and the time.
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 400,
                      display: { xs: 'none', sm: 'inline' },
                    }}
                  >
                    {' '}
                    @{post.author.username}
                  </Box>
                )}
              </Typography>

              <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                ·<Box component="span" sx={{ mx: 0.5 }} />
                <time dateTime={post.createdAt}>{timeAgo}</time>
                {post.isEdited && ` · ${t('post.edited')}`}
              </Typography>
            </Stack>

            {/* Place and scope as a line of text rather than a row of chips.
                Three chips per card turned every header into a wall of pills;
                the register keeps its badge, because that one has to be a
                colour, an icon and a word at once (spec §9.2). */}
            {/* The register badge leads the second line rather than sitting
                top-right: on a phone the badge, the name and the timestamp
                cannot share one row without the name shrinking to two letters.
                Place and scope read as text — three chips per card turned every
                header into a wall of pills — but the register keeps its badge,
                because that one has to be a colour, an icon and a word at once
                (spec §9.2). */}
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <TypeBadge type={post.type} size="small" />

              {/* Whether a business answered, and how far it got. A BAD post
                  that was answered and fixed reads nothing like one nobody
                  replied to, and a list that hides the difference is not
                  telling you the thing you came for. */}
              <PostBusinessMarkers post={post} />

              <Typography variant="caption" color="text.secondary">
                {post.countryCode && (
                  <Box component="span" aria-hidden sx={{ mr: 0.5 }}>
                    {post.countryFlag ?? flagEmoji(post.countryCode)}
                  </Box>
                )}
                {[post.town, post.countryName ?? post.countryCode].filter(Boolean).join(', ')}
                {post.countryCode && ' · '}
                {t(`scopes.${post.scope}.cardLabel`)}
              </Typography>
            </Stack>
          </Stack>

          <IconButton
            size="small"
            aria-label={t('post.options')}
            aria-haspopup="menu"
            onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
          >
            <MoreHorizRoundedIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={menuAnchor !== null}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                void navigator.clipboard?.writeText(`${window.location.origin}/posts/${post.id}`);
              }}
            >
              <ListItemIcon>
                <IosShareRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('post.copyLink')}</ListItemText>
            </MenuItem>

            {canReport && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setReportTarget({
                    kind: 'POST',
                    id: post.id,
                    label: post.title ?? post.body,
                  });
                }}
              >
                <ListItemIcon>
                  <FlagOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('safety.report')}</ListItemText>
              </MenuItem>
            )}

            {otherAuthor && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setBlockTarget({
                    id: otherAuthor.id,
                    username: otherAuthor.username,
                    displayName: otherAuthor.displayName,
                    isBlocked: false,
                  });
                }}
              >
                <ListItemIcon>
                  <BlockRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t('safety.blockUser', { username: otherAuthor.username })}
                </ListItemText>
              </MenuItem>
            )}

            {post.canEdit && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setEditing(true);
                }}
              >
                <ListItemIcon>
                  <EditOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('common.edit')}</ListItemText>
              </MenuItem>
            )}

            {post.canDelete && onDelete && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete(post);
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('common.delete')}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          {/* Siblings of the Menu, not children: MUI unmounts a Menu's
              children when it closes, which would take the dialog with it. */}
          {editing && <EditPostDialog post={post} open onClose={() => setEditing(false)} />}
          <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
          <BlockDialog target={blockTarget} onClose={() => setBlockTarget(null)} />
        </Stack>

        <Stack spacing={1}>
          {post.title && (
            <Typography
              id={`post-${post.id}-heading`}
              // The title is the point of the card, so it gets room to be one:
              // a step above the body rather than a bolder version of it.
              variant={expanded ? 'h2' : 'h3'}
              component={expanded ? 'h1' : headingLevel}
            >
              {expanded ? (
                post.title
              ) : (
                <Box
                  component={RouterLink}
                  to={`/posts/${post.id}`}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {post.title}
                </Box>
              )}
            </Typography>
          )}

          <PostBody text={post.body} expanded={expanded} />
        </Stack>

        {post.media.length > 0 && <PostMedia media={post.media} />}

        {post.tags.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {post.tags.map((tag) => (
              <Chip
                key={tag}
                size="small"
                variant="outlined"
                label={`#${tag}`}
                onClick={onTagClick ? () => onTagClick(tag) : undefined}
                clickable={Boolean(onTagClick)}
              />
            ))}
          </Stack>
        )}

        {/* Spec §8.11: the month's most upvoted ideal in its country. */}
        {post.isIdealOfMonth && (
          <Box>
            <Chip
              component={RouterLink}
              to="/ideals"
              clickable
              size="small"
              color="ideal"
              variant="outlined"
              icon={<EmojiEventsRoundedIcon fontSize="small" aria-hidden />}
              label={t('post.idealOfMonth')}
            />
          </Box>
        )}

        {/* Spec §4.3: a BAD post with proposals wears a gold chip. */}
        {post.type === 'BAD' && post.idealCount > 0 && (
          <Box>
            <Chip
              component={RouterLink}
              to={`/posts/${post.id}`}
              clickable
              size="small"
              color="ideal"
              variant="filled"
              icon={<AutoAwesomeRoundedIcon fontSize="small" aria-hidden />}
              label={t('post.idealCount', { count: post.idealCount })}
            />
          </Box>
        )}

        {post.linkedPost && (
          <Box
            component={RouterLink}
            to={`/posts/${post.linkedPost.id}`}
            sx={{
              display: 'block',
              textDecoration: 'none',
              color: 'inherit',
              p: 1.5,
              borderRadius: `${radius.sm}px`,
              border: 1,
              borderColor: (theme) => theme.palette.bad.border,
              backgroundColor: (theme) => theme.palette.bad.softer,
            }}
          >
            <Typography variant="caption" sx={{ color: 'bad.text', fontWeight: 600 }}>
              {t('post.answering')}
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              {post.linkedPost.title ?? post.linkedPost.body}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pt: 0.5 }}>
          <VoteButton
            count={post.upvoteCount}
            hasUpvoted={post.hasUpvoted}
            accent={register.accent}
            onToggle={() => toggleVote.mutate({ postId: post.id, hasUpvoted: post.hasUpvoted })}
            {...(disabledReason ? { disabledReason } : {})}
          />

          <Button
            component={RouterLink}
            to={`/posts/${post.id}`}
            size="small"
            variant="text"
            startIcon={<ChatBubbleOutlineRoundedIcon fontSize="small" />}
            aria-label={t('post.commentCount', { count: post.commentCount })}
            sx={{ color: 'text.secondary' }}
          >
            <Box component="span" aria-hidden>
              {post.commentCount}
            </Box>
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip
            title={disabledReason ?? (post.hasBookmarked ? t('post.unsave') : t('post.save'))}
          >
            <span>
              <IconButton
                size="small"
                disabled={Boolean(disabledReason)}
                onClick={() =>
                  toggleBookmark.mutate({
                    postId: post.id,
                    hasBookmarked: post.hasBookmarked,
                  })
                }
                aria-pressed={post.hasBookmarked}
                aria-label={post.hasBookmarked ? t('post.unsave') : t('post.save')}
                sx={{ color: post.hasBookmarked ? 'text.primary' : 'text.secondary' }}
              >
                {post.hasBookmarked ? (
                  <BookmarkRoundedIcon fontSize="small" />
                ) : (
                  <BookmarkBorderRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
